import { useEffect, useState, useMemo, useCallback } from "react";
import {
  FileText,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  Clock,
  X,
  Loader,
  User,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import { useSidebar } from "../../context/SidebarContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import ActiveApplications from "../../components/StaffPortal/ActiveApplications";
import TaskUpdateDialog from "../../components/StaffPortal/TaskUpdateDialog";
import { loanAPI } from "../../services/AdminPortal/loansAPI";
import { staffAPI } from "../../services/AdminPortal/staffAPI";
import { taskAPI } from "../../services/AdminPortal/taskAPI";
import { affiliateAPI } from "../../services/AdminPortal/affiliateAPI";
import { MappingStyles } from "../../utils/utils";
import { formatDate, formatTime } from "../../utils/utils";

const StaffDashboard = () => {
  const { sidebarExpanded, isMobile } = useSidebar();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [staffWithLoansData, setStaffWithLoansData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("allTime"); // For metrics cards
  const [chartPeriod, setChartPeriod] = useState("allTime"); // For lead conversion chart
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [createdStaff, setCreatedStaff] = useState([]);
  const [createdStaffLoading, setCreatedStaffLoading] = useState(false);
  const [createdAffiliatePartners, setCreatedAffiliatePartners] = useState([]);
  const [createdAffiliatePartnersLoading, setCreatedAffiliatePartnersLoading] = useState(false);
  const staffId = user?.staffId;

  const fetchTasks = async () => {
    if (!staffId) return;

    try {
      setTasksLoading(true);
      const response = await taskAPI.getStaffTasks(staffId);
      if (response.success) {
        setTasks(response.data.tasks || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setTasksLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === "COMPLETED") {
      return <CheckCircle size={16} className="text-green-500" />;
    } else if (status === "PENDING") {
      return (
        <div className="flex items-center gap-1 text-yellow-500">
          <AlertTriangle size={16} />
        </div>
      );
    } else if (status === "IN_PROGRESS") {
      return (
        <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      );
    } else if (status === "CANCELLED") {
      return <div className="w-4 h-4 rounded-full bg-red-500"></div>;
    } else {
      return null;
    }
  };

  const getPriorityBgColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-green-400/20";
      case "MEDIUM":
        return "bg-yellow-400/20";
      case "HIGH":
        return "bg-red-300/20";
      case "URGENT":
        return "bg-red-600/20";
      default:
        return "bg-[#131313]"; // Default color for unknown priority
    }
  };

  const handleTaskUpdate = async (taskId, updateData) => {
    try {
      const response = await taskAPI.updateTaskStatus(taskId, updateData);
      if (response.success) {
        // Refresh tasks
        await fetchTasks();
        setShowTaskDialog(false);
        setSelectedTask(null);
      }
    } catch (error) {
      console.error("Error updating task:", error);
      throw error;
    }
  };

  const openTaskDialog = (task) => {
    setSelectedTask(task);
    setShowTaskDialog(true);
  };
  // Data Fetching
  const fetchStaffData = async () => {
    if (!staffId) {
      setLoadingData(false);
      return;
    }
    try {
      setLoadingData(true);

      // 1. Fetch detailed staff information
      const staffDetailsResponse = await staffAPI.getStaffById({ staffId });

      // 2. Fetch assigned loans for the staff using loanAPI
      // This is where the data for metrics is fetched
      const assignedLoansResponse = await loanAPI.getLoansByStaffId(staffId);

      if (staffDetailsResponse.success && staffDetailsResponse.data) {
        setStaffWithLoansData({
          staffDetails: staffDetailsResponse.data,
          // Ensure assignedLoans is always an array, handling potential API response structures
          assignedLoans: Array.isArray(assignedLoansResponse)
            ? assignedLoansResponse
            : assignedLoansResponse?.data || [],
        });
        setDataError(null);
      } else {
        setDataError(
          staffDetailsResponse.message || "Failed to fetch staff details."
        );
      }
      // console.log(
      //   "staffDashboard.jsx - Staff Details Response:",
      //   staffDetailsResponse
      // );
      // console.log(
      //   "staffDashboard.jsx - Assigned Loans Response:",
      //   assignedLoansResponse
      // );
    } catch (error) {
      console.error("Error fetching staff data:", error);
      setDataError(error.message || "Failed to fetch staff data");
    } finally {
      setLoadingData(false);
    }
  };

  const fetchCreatedStaff = async () => {
    if (!user?.id) return;

    try {
      setCreatedStaffLoading(true);
      const response = await staffAPI.getStaffCreatedBy(user.id);
      if (response.success) {
        setCreatedStaff(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching created staff:", error);
    } finally {
      setCreatedStaffLoading(false);
    }
  };

  const fetchCreatedAffiliatePartners = async () => {
    if (!user?.id) return;

    try {
      setCreatedAffiliatePartnersLoading(true);
      const response = await affiliateAPI.getAffiliatePartnersCreatedBy(user.id);
      if (response.success) {
        setCreatedAffiliatePartners(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching created affiliate partners:", error);
    } finally {
      setCreatedAffiliatePartnersLoading(false);
    }
  };

  useEffect(() => {
    const hasLoggedIn = sessionStorage.getItem("justLoggedIn");
    if (hasLoggedIn === "true") {
      toast.success("Login successful");
      sessionStorage.removeItem("justLoggedIn");
    }

    fetchStaffData();
    fetchTasks();
    fetchCreatedStaff();
    fetchCreatedAffiliatePartners();
  }, [staffId, user?.id]);

  // Refresh data when returning from affiliate partner creation
  useEffect(() => {
    const refreshData = () => {
      fetchCreatedAffiliatePartners();
    };
    
    // Listen for focus event to refresh data when returning to dashboard
    window.addEventListener('focus', refreshData);
    
    return () => {
      window.removeEventListener('focus', refreshData);
    };
  }, []);

  // Data Filtering and Metric Calculation
  // These functions operate on the data fetched by fetchStaffData

  // Memoize helper functions to stabilize them for useMemo dependencies
  const filterLoansByPeriod = useCallback((loans, period) => {
    if (!loans) return [];
    const today = new Date();
    let startDate, endDate;

    switch (period) {
      case "today":
        startDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate()
        );
        // Set end date to the end of the day
        endDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate(),
          23,
          59,
          59,
          999
        );
        break;
      case "thisWeek": {
        const currentDay = today.getDay(); // 0 (Sun) to 6 (Sat)
        // Adjust for Sunday (0) being the start of the week in some locales, make Monday (1) the start
        const diff = currentDay === 0 ? -6 : -(currentDay - 1);
        startDate = new Date(
          today.getFullYear(),
          today.getMonth(),
          today.getDate() + diff
        );
        startDate.setHours(0, 0, 0, 0);

        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
        break;
      }
      case "thisMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        // Day 0 of next month is the last day of this month
        endDate = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;
      case "quarterly":
        // Last 3 months (current month + 2 previous months)
        startDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        endDate = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59,
          999
        );
        break;
      case "thisYear":
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31, 23, 59, 59, 999);
        break;
      case "allTime":
      default:
        return loans; // Return all loans if period is 'allTime' or undefined
    }
    // Ensure loan.createdAt is a valid Date object for comparison
    return loans.filter((loan) => {
      const loanDate = new Date(loan.createdAt);
      // Check if loanDate is valid before comparison
      return (
        !isNaN(loanDate.getTime()) &&
        loanDate >= startDate &&
        loanDate <= endDate
      );
    });
  }, []);

  const getLoansInDateRange = useCallback((loans, startDate, endDate) => {
    if (!loans || !startDate || !endDate) return [];

    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return loans.filter((loan) => {
      const loanDate = new Date(loan.createdAt);
      return !isNaN(loanDate.getTime()) && loanDate >= start && loanDate <= end;
    });
  }, []);

  const calculateMetrics = useCallback((filteredLoans) => {
    const totalApplications = filteredLoans.length;
    const approvedLoans = filteredLoans.filter(
      (loan) => loan.status?.toLowerCase() === "approved"
    ).length;
    const rejectedLoans = filteredLoans.filter(
      (loan) => loan.status?.toLowerCase() === "rejected"
    ).length;
    const pendingApplications = filteredLoans.filter((loan) =>
      // Include relevant statuses for 'pending'
      ["pending", "processing", "submitted", "in-review", "new-leads"].includes(
        loan.status?.toLowerCase()
      )
    ).length;
    // Ensure loan.principle is a number before adding
    const totalLoanAmount = filteredLoans.reduce(
      (sum, loan) => sum + (parseFloat(loan.principle) || 0),
      0
    );
    const leadConversionPercentage =
      totalApplications > 0 ? (approvedLoans / totalApplications) * 100 : 0;

    return {
      totalApplications,
      approvedLoans,
      rejectedLoans,
      pendingApplications,
      totalLoanAmount,
      leadConversionPercentage: parseFloat(leadConversionPercentage.toFixed(2)), // Format to 2 decimal places
    };
  }, []);

  // Use useMemo to recalculate metrics only when assignedLoans or selectedPeriod changes
  const {
    totalApplications,
    approvedLoans,
    rejectedLoans,
    pendingApplications,
    totalLoanAmount,
    leadConversionPercentage,
  } = useMemo(() => {
    const filteredLoans = filterLoansByPeriod(
      staffWithLoansData?.assignedLoans,
      selectedPeriod
    );
    // Pass an empty array if filteredLoans is null or undefined to avoid errors
    return calculateMetrics(filteredLoans || []);
  }, [
    staffWithLoansData,
    selectedPeriod,
    filterLoansByPeriod,
    calculateMetrics,
  ]); // Dependencies for recalculation

  // Calculate trend data (last 30 days vs previous 30 days)
  const trendData = useMemo(() => {
    if (!staffWithLoansData?.assignedLoans) {
      return {
        totalApplicationsTrend: 0,
        pendingApplicationsTrend: 0,
        approvedLoansTrend: 0,
        totalLoanAmountTrend: 0,
      };
    }

    const today = new Date();

    const currentTrendPeriodEndDate = new Date(today);
    const currentTrendPeriodStartDate = new Date(today);
    currentTrendPeriodStartDate.setDate(today.getDate() - 29); // Last 30 days including today

    const previousTrendPeriodEndDate = new Date(currentTrendPeriodStartDate);
    previousTrendPeriodEndDate.setDate(
      currentTrendPeriodStartDate.getDate() - 1
    );
    const previousTrendPeriodStartDate = new Date(previousTrendPeriodEndDate);
    previousTrendPeriodStartDate.setDate(
      previousTrendPeriodEndDate.getDate() - 29
    ); // 30 days before current trend period

    const loansForCurrentTrendPeriod = getLoansInDateRange(
      staffWithLoansData.assignedLoans,
      currentTrendPeriodStartDate,
      currentTrendPeriodEndDate
    );
    const loansForPreviousTrendPeriod = getLoansInDateRange(
      staffWithLoansData.assignedLoans,
      previousTrendPeriodStartDate,
      previousTrendPeriodEndDate
    );

    const metricsCurrentPeriod = calculateMetrics(loansForCurrentTrendPeriod);
    const metricsPreviousPeriod = calculateMetrics(loansForPreviousTrendPeriod);

    const calculateIndividualTrend = (current, previous) => {
      if (previous === 0) {
        return current > 0 ? Infinity : 0; // Indicates a new appearance or no change from zero
      }
      return ((current - previous) / previous) * 100;
    };

    return {
      totalApplicationsTrend: calculateIndividualTrend(
        metricsCurrentPeriod.totalApplications,
        metricsPreviousPeriod.totalApplications
      ),
      pendingApplicationsTrend: calculateIndividualTrend(
        metricsCurrentPeriod.pendingApplications,
        metricsPreviousPeriod.pendingApplications
      ),
      approvedLoansTrend: calculateIndividualTrend(
        metricsCurrentPeriod.approvedLoans,
        metricsPreviousPeriod.approvedLoans
      ),
      rejectedLoansTrend: calculateIndividualTrend(
        metricsCurrentPeriod.rejectedLoans,
        metricsPreviousPeriod.rejectedLoans
      ),
      totalLoanAmountTrend: calculateIndividualTrend(
        metricsCurrentPeriod.totalLoanAmount,
        metricsPreviousPeriod.totalLoanAmount
      ),
    };
  }, [
    staffWithLoansData?.assignedLoans,
    getLoansInDateRange,
    calculateMetrics,
  ]);

  // Memoize the metrics array itself
  const metrics = useMemo(
    () => [
      {
        title: "TOTAL APPLICATIONS",
        count: totalApplications.toLocaleString("en-IN"), // Format number with commas
        color: "bg-yellow-500",
        icon: <FileText className="w-4 h-4" />,
        trendValue: trendData.totalApplicationsTrend,
      },
      {
        title: "PENDING APPLICATIONS",
        count: pendingApplications.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <AlertTriangle className="w-4 h-4" />,
        trendValue: trendData.pendingApplicationsTrend,
      },
      {
        title: "APPROVED LOANS",
        count: approvedLoans.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <CheckCircle className="w-4 h-4" />,
        trendValue: trendData.approvedLoansTrend,
      },
      {
        title: "REJECTED LOANS",
        count: rejectedLoans.toLocaleString("en-IN"),
        color: "bg-yellow-500",
        icon: <CheckCircle className="w-4 h-4" />,
        trendValue: trendData.rejectedLoansTrend,
      },
      {
        title: "TOTAL LOAN AMOUNT",
        // Format as currency (Indian Rupees)
        count: `₹${totalLoanAmount.toLocaleString("en-IN", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`,
        color: "bg-yellow-500",
        icon: <FileText className="w-4 h-4" />,
        trendValue: trendData.totalLoanAmountTrend,
      },
    ],
    [
      totalApplications,
      pendingApplications,
      approvedLoans,
      rejectedLoans,
      totalLoanAmount,
      trendData,
    ]
  ); // Dependencies for metrics array

  // Memoize the filtered applications specifically for the table
  const filteredApplicationsForTable = useMemo(() => {
    return filterLoansByPeriod(
      staffWithLoansData?.assignedLoans,
      selectedPeriod
    );
  }, [staffWithLoansData, selectedPeriod]); // Dependencies for table data

  // Separate filtered data for lead conversion chart
  const chartFilteredData = useMemo(() => {
    const filteredLoans = filterLoansByPeriod(
      staffWithLoansData?.assignedLoans,
      chartPeriod
    );
    return calculateMetrics(filteredLoans || []);
  }, [staffWithLoansData, chartPeriod, filterLoansByPeriod, calculateMetrics]);

  // Mapping for staff roles
  const roleMapping = {
    admin: "Admin",
    loanOfficer: "Loan Officer",
    subAdmin: "Sub Admin",
    hr: "HR",
    "credit-manager": "Credit Manager",
    marketing: "Marketing",
    sales: "Sales",
    strategy: "Strategy",
  };

  // Handler for period selection change (metrics cards)
  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  // Handler for chart period selection change
  const handleChartPeriodChange = (event) => {
    setChartPeriod(event.target.value);
  };

  // Options for the period filter dropdown
  const periodOptions = [
    { value: "allTime", label: "All Time" },
    { value: "thisYear", label: "Yearly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "thisMonth", label: "Monthly" },
  ];
  // Handler for viewing a specific application
  const handleViewApplication = (leadId) => {
    navigate(`/staff/loan-applications/${leadId}`);
  };

  // Loading and Error states rendering
  if (loadingData) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (dataError) {
    return (
      <div className="min-h-screen bg-black text-red-500 p-4">
        Error: {dataError}
      </div>
    );
  }

  // Main Dashboard Render
  return (
    <div className="min-h-screen bg-black text-white   ">
      {/*add buttons */}
      <div className="flex justify-end gap-2 pr-1">

        <button
          onClick={() => navigate('/staff/add-affiliate-partner')}
          className="bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-semibold px-4 py-2 rounded flex items-center gap-2"
        >
           <Users size={16} /> Add Affiliate Partner
        </button>
      </div>
      {/* Staff Profile Section */}
      {staffWithLoansData?.staffDetails && (
        <div className="bg-[#131313] rounded-lg p-3 mx-2 my-4 md:p-4 md:m-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-orange-600 flex items-center justify-center text-white text-lg md:text-xl font-bold overflow-hidden flex-shrink-0">
                <img
                  src={
                    staffWithLoansData.staffDetails.profileImage ||
                    `https://ui-avatars.com/api/?name=${staffWithLoansData.staffDetails.name}&background=fdba74&color=854d0e&size=128`
                  }
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg md:text-xl font-bold capitalize truncate">
                  {staffWithLoansData.staffDetails.name}
                </h2>
                <div className="text-xs md:text-sm text-gray-400">
                  STAFF ID 
                  {staffWithLoansData.staffDetails.staffId ||
                    staffWithLoansData.staffDetails._id}
                </div>
                {/* <span className="text-yellow-500 text-xs md:text-sm">
                  {staffWithLoansData.staffDetails.performanceRating || "0"}% Performance Rating
                </span> */}
              </div>
            </div>
          </div>
          
          {/* Staff Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <div>
              <div className="text-xs text-gray-400">Role</div>
              <div className="text-sm">
                {roleMapping[staffWithLoansData.staffDetails.role] ||
                  staffWithLoansData.staffDetails.role}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Contact Number</div>
              <div className="text-sm">
                {staffWithLoansData.staffDetails.phone ||
                  staffWithLoansData.staffDetails.phoneNumber}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Email ID</div>
              <div className="text-sm truncate">
                {staffWithLoansData.staffDetails.email}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Date of Joining</div>
              <div className="text-sm">
                {staffWithLoansData.staffDetails.createdAt
                  ? new Date(
                      staffWithLoansData.staffDetails.createdAt
                    ).toLocaleDateString("en-GB")
                  : "N/A"}
              </div>
            </div>

            {/* ✅ Branch Details */}
            <div>
              <div className="text-xs text-gray-400">Branch Name</div>
              <div className="text-sm">
                {staffWithLoansData.staffDetails.branchName || "N/A"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Branch Type</div>
              <div className="text-sm">
                {staffWithLoansData.staffDetails.branchType === "district"
                  ? "District Level"
                  : staffWithLoansData.staffDetails.branchType === "mandal"
                  ? "Mandal Level"
                  : staffWithLoansData.staffDetails.branchType === "village"
                  ? "Village Level"
                  : staffWithLoansData.staffDetails.branchType || "N/A"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">Branch Location</div>
              <div className="text-sm">
                {staffWithLoansData.staffDetails.branchLocation || "N/A"}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-400">
                Montly No Of TagetLoans
              </div>
              <div className="text-sm">
                {staffWithLoansData?.staffDetails?.targetLoans || "N/A"}
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="p-4">
        {/* Filter Dropdown and Action Buttons */}
        <div className="mb-4 flex justify-end items-center">
          <div className="relative">
            <select
              value={selectedPeriod}
              onChange={handlePeriodChange}
              className="bg-[#131313] text-sm px-3 py-2 rounded border border-[#333333] focus:border-yellow-500 focus:outline-none appearance-none pr-8"
            >
              {periodOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={16}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none"
            />
          </div>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {metrics.map((metric, index) => (
            <div
              key={index}
              className="bg-[#131313] rounded-lg p-4 border border-[#333333]"
            >
              <div className="flex justify-between mb-1">
                <h3 className="text-xs text-gray-400">{metric.title}</h3>
                <div
                  className={`${metric.color} rounded-full p-2 h-8 w-8 flex items-center justify-center`}
                >
                  {metric.icon}
                </div>
              </div>
              <p className="text-xl font-semibold">{metric.count}</p>
              <p className="text-xs mt-1 text-gray-500">
                {periodOptions
                  .find((opt) => opt.value === selectedPeriod)
                  ?.label.toLowerCase() || "filter"}
              </p>
            </div>
          ))}
        </div>

        {/* Created Staff and Affiliate Partners Cards
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
         
          <div className="bg-[#131313] rounded-lg p-4 border border-[#333333]">
            <div className="flex justify-between mb-1">
              <h3 className="text-xs text-gray-400">STAFF CREATED</h3>
              <div className="bg-yellow-400 rounded-full p-2 h-8 w-8 flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </div>
            <p className="text-xl font-semibold">
              {createdStaffLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-500"></div>
              ) : (
                createdStaff.length
              )}
            </p>
            <p className="text-xs mt-1 text-gray-500">staff members</p>
          </div>
          
        
          <div className="bg-[#131313] rounded-lg p-4 border border-[#333333]">
            <div className="flex justify-between mb-1">
              <h3 className="text-xs text-gray-400">AFFILIATE PARTNERS CREATED</h3>
              <div className="bg-yellow-400 rounded-full p-2 h-8 w-8 flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
            </div>
            <p className="text-xl font-semibold">
              {createdAffiliatePartnersLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-green-500"></div>
              ) : (
                createdAffiliatePartners.length
              )}
            </p>
            <p className="text-xs mt-1 text-gray-500">affiliate partners</p>
          </div>
        </div> */}

        {/* Two Column Layout for Chart and Activities */}
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 w-full">
          {/* Lead Conversion Chart */}
          <div className="bg-[#131313] rounded-lg p-4 border border-[#333333] lg:col-span-1 w-full max-w-md mx-auto lg:mx-0 lg:max-w-none overflow-hidden">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm">LEAD CONVERSION</h3>
              <div className="relative">
                {/* Period Filter Dropdown */}
                <select
                  value={chartPeriod}
                  onChange={handleChartPeriodChange}
                  className="bg-[#131313] text-xs px-2 py-1 rounded appearance-none pr-6 border border-[#333333] focus:border-yellow-500 focus:outline-none"
                >
                  {periodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {/* Dropdown Icon */}
                <ChevronDown
                  size={16}
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none"
                />
              </div>
            </div>

            {/* Circular Progress Bar for Conversion */}
            <div className="flex flex-col items-center justify-center">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  {/* Background Circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#333" // Background stroke color
                    strokeWidth="8"
                  />
                  {/* Progress Circle */}
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="#F59E0B" // Progress stroke color (Yellow)
                    strokeWidth="8"
                    strokeDasharray="251.2" // Circumference of a circle with radius 40 (2 * PI * 40)
                    strokeDashoffset={
                      251.2 *
                      (1 - chartFilteredData.leadConversionPercentage / 100)
                    } // Offset based on percentage
                    transform="rotate(-90 50 50)" // Start from the top
                  />
                  {/* Percentage Text */}
                  <text
                    x="50"
                    y="50"
                    dominantBaseline="middle"
                    textAnchor="middle"
                    fill="#fff" // Text color
                    fontSize="14"
                    fontWeight="bold"
                  >
                    <span>Total</span>
                    {chartFilteredData.totalApplications}
                  </text>
                </svg>
              </div>

              {/* Conversion Details */}
              <div className="grid grid-cols-1 gap-4 mt-6 w-full text-center">
                <div>
                  <p className="text-gray-400 text-xs">Total Leads</p>
                  <p className="text-xl font-semibold">
                    {chartFilteredData.totalApplications}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Converted Leads</p>
                  <p className="text-xl font-semibold">
                    {chartFilteredData.approvedLoans}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">Lead Conversion</p>
                  <p className="text-xl font-semibold">
                    {chartFilteredData.leadConversionPercentage}%
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Tasks Section */}
          <div className="bg-[#131313] rounded-lg p-4 border border-[#333333] col-span-3">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium">MY TASKS</h3>
              <div className="text-xs text-gray-400">
                {tasks.length} task{tasks.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* Tasks list */}
            <div className="max-h-[70vh] overflow-y-auto space-y-3">
              {tasksLoading ? (
                <div className="text-center text-gray-500 py-6">
                  <Loader className="animate-spin mx-auto mb-2" size={20} />
                  Loading tasks...
                </div>
              ) : tasks.length > 0 ? (
                tasks.map((task) => (
                  <div
                    key={task._id}
                    className={`flex items-center justify-between p-3 hover:bg-[#131313] rounded transition cursor-pointer ${getPriorityBgColor(
                      task.priority
                    )}`}
                    onClick={() => openTaskDialog(task)}
                  >
                    <div className="flex items-center gap-3">
                      {/* Status Icon */}
                      <div className="flex-shrink-0">
                        {getStatusIcon(task.status)}
                      </div>

                      {/* Task Info */}
                      <div>
                        <p className="text-sm text-white font-medium">
                          {task.title}
                        </p>
                        <p className="text-xs text-gray-400">{task.priority}</p>
                      </div>
                    </div>

                    {/* Status and Update Button */}
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded text-xs ${MappingStyles(
                          task.status
                        )}`}
                      >
                        {task.status.replace("_", " ")}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openTaskDialog(task);
                        }}
                        className="text-yellow-500 text-xs hover:text-yellow-400"
                      >
                        Update Status
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-6 text-sm">
                  No tasks assigned yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Applications Table */}
        <div className="mt-6">
          <ActiveApplications
            activeApplications={filteredApplicationsForTable || []} // Pass filtered data
            handleViewApplication={handleViewApplication}
          />
        </div>
      </main>

      {/* Task Update Dialog */}
      <TaskUpdateDialog
        isOpen={showTaskDialog}
        onClose={() => {
          setShowTaskDialog(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onUpdate={handleTaskUpdate}
      />
    </div>
  );
};

export default StaffDashboard;
